import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'clsx';

import CollapsibleToggle from './components/collapsible-toggle/collapsible-toggle';
import usePrevious from '../form/use-previous';
import Typography from '../typography/typography';
import { collapseType, toggleType } from './collapsible-types';

import styles from './collapsible.scss';

interface CollapsibleProps {
  className?: string;
  collapseType?: collapseType;
  customLabel?: string | null;
  description?: string;
  hideToggle?: boolean;
  id?: string;
  isDisabled?: boolean;
  isOpen?: boolean;
  isOpenByDefault?: boolean;
  label?: string | JSX.Element;
  onToggle?: (isOpen?: boolean) => void;
  shouldExpandUp?: boolean;
  toggleType?: toggleType;
}

function Collapsible(props: React.PropsWithChildren<CollapsibleProps>) {
  const {
    children,
    className = '',
    collapseType,
    customLabel,
    description,
    hideToggle,
    id,
    isDisabled = false,
    isOpen: isOpenProp,
    isOpenByDefault = false,
    label,
    onToggle,
    shouldExpandUp = false,
    toggleType
  } = props;
  const [isOpenInternal, setIsOpenInternal] = useState(isOpenByDefault);
  const prevIsOpenInternal = usePrevious(isOpenInternal);
  const prevIsOpenProp = usePrevious(isOpenProp);
  const [contentHeight, setContentHeight] = useState<number | string>(
    isOpenByDefault ? 'auto' : 0
  );
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const expandTime = 340;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const isOpen = typeof isOpenProp === 'boolean' ? isOpenProp : isOpenInternal;

  const handleOpenClose = useCallback(
    (isOpen: boolean) => {
      const contentElement = contentRef.current;

      if (!contentElement) return;

      const measuredHeight = contentElement.clientHeight;

      if (isOpen) {
        setContentHeight(measuredHeight);
        openTimerRef.current = setTimeout(() => {
          setContentHeight('auto');
        }, expandTime);
      } else {
        if (openTimerRef.current) {
          clearTimeout(openTimerRef.current);
          openTimerRef.current = undefined;
        }

        if (contentHeight) {
          setContentHeight(measuredHeight);
          setTimeout(() => {
            setContentHeight(0);
          }, 30);
        } else {
          setContentHeight(0);
        }
      }
    },
    [contentHeight, expandTime]
  );

  function toggleCollapsible() {
    const hasIsOpenProp = typeof isOpenProp === 'boolean';

    if (typeof onToggle === 'function') {
      if (hasIsOpenProp) {
        onToggle(!isOpenProp);
      } else {
        onToggle(!isOpenInternal);
      }
    }

    if (!hasIsOpenProp) {
      setIsOpenInternal(!isOpenInternal);
    }
  }

  useEffect(() => {
    if (
      typeof isOpenProp !== 'boolean' &&
      prevIsOpenInternal !== isOpenInternal
    ) {
      handleOpenClose(isOpenInternal);
    }
  }, [isOpenInternal, isOpenProp, handleOpenClose, prevIsOpenInternal]);

  useEffect(() => {
    if (typeof isOpenProp === 'boolean' && prevIsOpenProp !== isOpenProp) {
      handleOpenClose(isOpenProp);
    }
  }, [isOpenProp, handleOpenClose, prevIsOpenProp]);

  return (
    <div
      className={cn(styles['container'], `collapsible ${className}`)}
      id={id}
    >
      {!shouldExpandUp && !hideToggle && (
        <div className="collapsible__label">
          <CollapsibleToggle
            className={className}
            collapseType={collapseType}
            isDisabled={isDisabled}
            isOpen={isOpen}
            label={label}
            toggleCollapsible={toggleCollapsible}
            toggleType={toggleType}
          />
          {customLabel && (
            <Typography className="collapsible__custom-label">
              {customLabel}
            </Typography>
          )}
        </div>
      )}
      <div
        className={cn(
          styles['content-wrapper'],
          'collapsible__content-wrapper'
        )}
        style={{
          height: contentHeight
        }}
      >
        <div
          className={cn(styles['content'], 'collapsible__content')}
          ref={contentRef}
        >
          {description && (
            <Typography
              className={cn(styles['description'], 'collapsible__description')}
            >
              {description}
            </Typography>
          )}
          {children}
        </div>
      </div>
      {shouldExpandUp && !hideToggle && (
        <CollapsibleToggle
          className={className}
          collapseType={collapseType}
          isDisabled={isDisabled}
          isOpen={isOpen}
          label={label}
          toggleCollapsible={toggleCollapsible}
        />
      )}
    </div>
  );
}

export default Collapsible;
